import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { clsx } from 'clsx'

// Pine Script syntax definitions
const PINE_KEYWORDS = [
  'strategy', 'indicator', 'study', 'plot', 'plotshape', 'plotchar', 'plotarrow',
  'plotbar', 'plotcandle', 'fill', 'hline', 'bgcolor', 'barcolor', 'alertcondition',
  'if', 'else', 'for', 'while', 'switch', 'case', 'default', 'break', 'continue',
  'var', 'varip', 'export', 'import', 'type', 'method', 'and', 'or', 'not', 'true', 'false',
  'na', 'input', 'color', 'series', 'simple', 'const', 'matrix', 'array', 'map', 'log'
]

const PINE_FUNCTIONS = [
  'ta.sma', 'ta.ema', 'ta.wma', 'ta.vwma', 'ta.rma', 'ta.rsi', 'ta.macd', 'ta.stoch',
  'ta.bb', 'ta.atr', 'ta.tr', 'ta.cci', 'ta.mfi', 'ta.obv', 'ta.adx', 'ta.dmi',
  'ta.pivothigh', 'ta.pivotlow', 'ta.highest', 'ta.lowest', 'ta.crossover', 'ta.crossunder',
  'ta.change', 'ta.mom', 'ta.roc', 'ta.valuewhen', 'ta.barssince', 'ta.cum', 'ta.falling', 'ta.rising',
  'math.abs', 'math.avg', 'math.ceil', 'math.floor', 'math.round', 'math.max', 'math.min',
  'math.pow', 'math.sqrt', 'math.log', 'math.exp', 'math.sin', 'math.cos', 'math.tan',
  'str.tostring', 'str.format', 'str.length', 'str.contains', 'str.replace',
  'array.new_float', 'array.push', 'array.pop', 'array.get', 'array.set', 'array.size',
  'request.security', 'request.financial', 'request.quandl', 'request.earnings',
  'strategy.entry', 'strategy.exit', 'strategy.close', 'strategy.close_all',
  'strategy.order', 'strategy.cancel', 'strategy.cancel_all',
  'alert', 'label.new', 'line.new', 'box.new', 'table.new'
]

const PINE_VARIABLES = [
  'open', 'high', 'low', 'close', 'volume', 'time', 'time_close', 'bar_index',
  'timenow', 'syminfo.ticker', 'syminfo.tickerid', 'syminfo.timezone',
  'strategy.position_size', 'strategy.position_avg_price', 'strategy.equity',
  'strategy.netprofit', 'strategy.grossprofit', 'strategy.grossloss',
  'strategy.openprofit', 'strategy.closedtrades', 'strategy.wintrades', 'strategy.losstrades'
]

const PINE_COLORS = [
  'color.red', 'color.green', 'color.blue', 'color.white', 'color.black',
  'color.yellow', 'color.orange', 'color.purple', 'color.aqua', 'color.fuchsia',
  'color.lime', 'color.maroon', 'color.navy', 'color.olive', 'color.silver', 'color.teal'
]

interface PineScriptEditorProps {
  initialCode?: string
  onChange?: (code: string) => void
  onCompile?: (code: string) => void
  onRun?: (code: string) => void
  readOnly?: boolean
  height?: string
  showToolbar?: boolean
  showLineNumbers?: boolean
}

interface EditorError {
  line: number
  column: number
  message: string
  type: 'error' | 'warning'
}

interface AutocompleteItem {
  label: string
  type: 'keyword' | 'function' | 'variable' | 'color'
  description?: string
}

export function PineScriptEditor({
  initialCode = defaultPineScript,
  onChange,
  onCompile,
  onRun,
  readOnly = false,
  height = '100%',
  showToolbar = true,
  showLineNumbers = true,
}: PineScriptEditorProps) {
  const [code, setCode] = useState(initialCode)
  const [errors, setErrors] = useState<EditorError[]>([])
  const [warnings, setWarnings] = useState<EditorError[]>([])
  const [showAutocomplete, setShowAutocomplete] = useState(false)
  const [autocompleteItems, setAutocompleteItems] = useState<AutocompleteItem[]>([])
  const [autocompleteIndex, setAutocompleteIndex] = useState(0)
  const [cursorPosition, setCursorPosition] = useState({ line: 1, column: 1 })
  const [selectedText, setSelectedText] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const highlightRef = useRef<HTMLDivElement>(null)

  // Syntax highlight the code
  const highlightedCode = useMemo(() => highlightPineScript(code), [code])

  // Line numbers
  const lineNumbers = useMemo(() => {
    const lines = code.split('\n')
    return lines.map((_, i) => i + 1)
  }, [code])

  // Handle code change
  const handleCodeChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newCode = e.target.value
    setCode(newCode)
    onChange?.(newCode)
    
    // Basic validation
    validateCode(newCode)
    
    // Check for autocomplete trigger
    const cursorPos = e.target.selectionStart
    const textBeforeCursor = newCode.slice(0, cursorPos)
    const lastWord = textBeforeCursor.match(/[\w.]+$/)?.[0] || ''
    
    if (lastWord.length >= 2) {
      const suggestions = getAutocompleteSuggestions(lastWord)
      setAutocompleteItems(suggestions)
      setShowAutocomplete(suggestions.length > 0)
      setAutocompleteIndex(0)
    } else {
      setShowAutocomplete(false)
    }
  }, [onChange])

  // Validate code (simple validation)
  const validateCode = useCallback((code: string) => {
    const newErrors: EditorError[] = []
    const newWarnings: EditorError[] = []
    const lines = code.split('\n')
    
    lines.forEach((line, i) => {
      const lineNum = i + 1
      
      // Check for unclosed strings
      const singleQuotes = (line.match(/'/g) || []).length
      const doubleQuotes = (line.match(/"/g) || []).length
      if (singleQuotes % 2 !== 0) {
        newErrors.push({ line: lineNum, column: 1, message: 'Unclosed string literal', type: 'error' })
      }
      if (doubleQuotes % 2 !== 0) {
        newErrors.push({ line: lineNum, column: 1, message: 'Unclosed string literal', type: 'error' })
      }
      
      // Check for unbalanced parentheses
      const openParens = (line.match(/\(/g) || []).length
      const closeParens = (line.match(/\)/g) || []).length
      if (openParens !== closeParens) {
        newWarnings.push({ line: lineNum, column: 1, message: 'Unbalanced parentheses', type: 'warning' })
      }
      
      // Check for deprecated functions
      if (line.includes('security(')) {
        newWarnings.push({ line: lineNum, column: line.indexOf('security'), message: 'security() is deprecated, use request.security() instead', type: 'warning' })
      }
      if (line.includes('study(')) {
        newWarnings.push({ line: lineNum, column: line.indexOf('study'), message: 'study() is deprecated, use indicator() instead', type: 'warning' })
      }
    })
    
    // Check for required declarations
    if (!code.includes('//@version=')) {
      newWarnings.push({ line: 1, column: 1, message: 'Missing version annotation (//@version=5)', type: 'warning' })
    }
    if (!code.includes('strategy(') && !code.includes('indicator(') && !code.includes('study(')) {
      newErrors.push({ line: 1, column: 1, message: 'Script must declare strategy() or indicator()', type: 'error' })
    }
    
    setErrors(newErrors)
    setWarnings(newWarnings)
  }, [])

  // Get autocomplete suggestions
  const getAutocompleteSuggestions = useCallback((prefix: string): AutocompleteItem[] => {
    const lowerPrefix = prefix.toLowerCase()
    const suggestions: AutocompleteItem[] = []
    
    PINE_KEYWORDS.forEach(k => {
      if (k.toLowerCase().startsWith(lowerPrefix)) {
        suggestions.push({ label: k, type: 'keyword' })
      }
    })
    
    PINE_FUNCTIONS.forEach(f => {
      if (f.toLowerCase().includes(lowerPrefix)) {
        suggestions.push({ label: f, type: 'function', description: getPineFunctionDescription(f) })
      }
    })
    
    PINE_VARIABLES.forEach(v => {
      if (v.toLowerCase().startsWith(lowerPrefix)) {
        suggestions.push({ label: v, type: 'variable' })
      }
    })
    
    PINE_COLORS.forEach(c => {
      if (c.toLowerCase().startsWith(lowerPrefix)) {
        suggestions.push({ label: c, type: 'color' })
      }
    })
    
    return suggestions.slice(0, 10)
  }, [])

  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Tab key for indentation
    if (e.key === 'Tab') {
      e.preventDefault()
      const start = e.currentTarget.selectionStart
      const end = e.currentTarget.selectionEnd
      const newCode = code.substring(0, start) + '    ' + code.substring(end)
      setCode(newCode)
      onChange?.(newCode)
      
      // Move cursor
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = textareaRef.current.selectionEnd = start + 4
        }
      }, 0)
    }
    
    // Autocomplete navigation
    if (showAutocomplete) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setAutocompleteIndex(prev => Math.min(prev + 1, autocompleteItems.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setAutocompleteIndex(prev => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        insertAutocomplete(autocompleteItems[autocompleteIndex])
      } else if (e.key === 'Escape') {
        setShowAutocomplete(false)
      }
    }
    
    // Compile shortcut (Ctrl/Cmd + Enter)
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault()
      onCompile?.(code)
    }
    
    // Run shortcut (Ctrl/Cmd + Shift + Enter)
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'Enter') {
      e.preventDefault()
      onRun?.(code)
    }
  }, [code, showAutocomplete, autocompleteItems, autocompleteIndex, onChange, onCompile, onRun])

  // Insert autocomplete selection
  const insertAutocomplete = useCallback((item: AutocompleteItem) => {
    if (!textareaRef.current) return
    
    const cursorPos = textareaRef.current.selectionStart
    const textBeforeCursor = code.slice(0, cursorPos)
    const textAfterCursor = code.slice(cursorPos)
    const lastWordMatch = textBeforeCursor.match(/[\w.]+$/)
    const lastWord = lastWordMatch?.[0] || ''
    const wordStart = cursorPos - lastWord.length
    
    let insertText = item.label
    if (item.type === 'function') {
      insertText += '()'
    }
    
    const newCode = code.slice(0, wordStart) + insertText + textAfterCursor
    setCode(newCode)
    onChange?.(newCode)
    setShowAutocomplete(false)
    
    // Move cursor
    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = wordStart + insertText.length - (item.type === 'function' ? 1 : 0)
        textareaRef.current.selectionStart = textareaRef.current.selectionEnd = newPos
        textareaRef.current.focus()
      }
    }, 0)
  }, [code, onChange])

  // Sync scroll between textarea and highlight
  const handleScroll = useCallback((e: React.UIEvent<HTMLTextAreaElement>) => {
    if (highlightRef.current) {
      highlightRef.current.scrollTop = e.currentTarget.scrollTop
      highlightRef.current.scrollLeft = e.currentTarget.scrollLeft
    }
  }, [])

  // Update cursor position
  const handleSelect = useCallback((e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget
    const pos = textarea.selectionStart
    const textBeforeCursor = code.slice(0, pos)
    const lines = textBeforeCursor.split('\n')
    const line = lines.length
    const column = lines[lines.length - 1].length + 1
    setCursorPosition({ line, column })
    
    if (textarea.selectionStart !== textarea.selectionEnd) {
      setSelectedText(code.slice(textarea.selectionStart, textarea.selectionEnd))
    } else {
      setSelectedText('')
    }
  }, [code])

  return (
    <div className="flex flex-col h-full bg-[#0d1117] rounded-lg overflow-hidden" style={{ height }}>
      {/* Toolbar */}
      {showToolbar && (
        <div className="flex items-center justify-between px-4 py-2 bg-[#161b22] border-b border-[#30363d]">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-[#8b949e]">Pine Script Editor</span>
            <span className="text-xs text-[#484f58]">v5</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onCompile?.(code)}
              disabled={errors.length > 0}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium rounded transition-colors flex items-center gap-2',
                errors.length > 0
                  ? 'bg-[#21262d] text-[#484f58] cursor-not-allowed'
                  : 'bg-[#238636] text-white hover:bg-[#2ea043]'
              )}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Compile
            </button>
            <button
              onClick={() => onRun?.(code)}
              disabled={errors.length > 0}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium rounded transition-colors flex items-center gap-2',
                errors.length > 0
                  ? 'bg-[#21262d] text-[#484f58] cursor-not-allowed'
                  : 'bg-[#1f6feb] text-white hover:bg-[#388bfd]'
              )}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Add to Chart
            </button>
          </div>
        </div>
      )}

      {/* Editor Area */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Line Numbers */}
        {showLineNumbers && (
          <div className="w-12 bg-[#0d1117] border-r border-[#21262d] text-right py-2 pr-3 text-[#484f58] text-sm font-mono select-none overflow-hidden">
            {lineNumbers.map((num) => (
              <div
                key={num}
                className={clsx(
                  'leading-6',
                  errors.some(e => e.line === num) && 'text-[#f85149]',
                  warnings.some(w => w.line === num) && 'text-[#d29922]'
                )}
              >
                {num}
              </div>
            ))}
          </div>
        )}

        {/* Code Area */}
        <div className="flex-1 relative overflow-hidden">
          {/* Syntax Highlighted Layer */}
          <div
            ref={highlightRef}
            className="absolute inset-0 p-2 font-mono text-sm leading-6 whitespace-pre-wrap overflow-auto pointer-events-none"
            style={{ color: 'transparent', caretColor: 'transparent' }}
            dangerouslySetInnerHTML={{ __html: highlightedCode }}
          />
          
          {/* Textarea (editable layer) */}
          <textarea
            ref={textareaRef}
            value={code}
            onChange={handleCodeChange}
            onKeyDown={handleKeyDown}
            onScroll={handleScroll}
            onSelect={handleSelect}
            readOnly={readOnly}
            spellCheck={false}
            className="absolute inset-0 w-full h-full p-2 font-mono text-sm leading-6 bg-transparent text-transparent caret-white resize-none outline-none"
            style={{ caretColor: '#00d4ff' }}
          />
          
          {/* Autocomplete Dropdown */}
          {showAutocomplete && (
            <AutocompleteDropdown
              items={autocompleteItems}
              selectedIndex={autocompleteIndex}
              onSelect={insertAutocomplete}
              position={getAutocompletePosition()}
            />
          )}
        </div>
      </div>

      {/* Status Bar */}
      <div className="flex items-center justify-between px-4 py-1.5 bg-[#161b22] border-t border-[#30363d] text-xs text-[#8b949e]">
        <div className="flex items-center gap-4">
          <span>Ln {cursorPosition.line}, Col {cursorPosition.column}</span>
          {selectedText && <span>{selectedText.length} selected</span>}
        </div>
        <div className="flex items-center gap-4">
          {errors.length > 0 && (
            <span className="text-[#f85149] flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {errors.length} error{errors.length > 1 ? 's' : ''}
            </span>
          )}
          {warnings.length > 0 && (
            <span className="text-[#d29922] flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {warnings.length} warning{warnings.length > 1 ? 's' : ''}
            </span>
          )}
          <span>Pine Script v5</span>
        </div>
      </div>

      {/* Error Panel */}
      {(errors.length > 0 || warnings.length > 0) && (
        <div className="max-h-32 overflow-y-auto border-t border-[#30363d] bg-[#161b22]">
          {errors.map((error, i) => (
            <div key={`error-${i}`} className="px-4 py-1.5 flex items-center gap-2 text-xs text-[#f85149] hover:bg-[#21262d] cursor-pointer">
              <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-[#8b949e]">Ln {error.line}:</span>
              <span>{error.message}</span>
            </div>
          ))}
          {warnings.map((warning, i) => (
            <div key={`warning-${i}`} className="px-4 py-1.5 flex items-center gap-2 text-xs text-[#d29922] hover:bg-[#21262d] cursor-pointer">
              <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-[#8b949e]">Ln {warning.line}:</span>
              <span>{warning.message}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  function getAutocompletePosition() {
    // Simplified position calculation
    return { top: (cursorPosition.line * 24) + 8, left: 60 }
  }
}

// Autocomplete dropdown component
function AutocompleteDropdown({
  items,
  selectedIndex,
  onSelect,
  position,
}: {
  items: AutocompleteItem[]
  selectedIndex: number
  onSelect: (item: AutocompleteItem) => void
  position: { top: number; left: number }
}) {
  return (
    <div
      className="absolute z-50 bg-[#161b22] border border-[#30363d] rounded-md shadow-xl overflow-hidden min-w-[200px] max-w-[300px]"
      style={{ top: position.top, left: position.left }}
    >
      {items.map((item, i) => (
        <div
          key={item.label}
          onClick={() => onSelect(item)}
          className={clsx(
            'px-3 py-1.5 flex items-center gap-2 cursor-pointer',
            i === selectedIndex ? 'bg-[#1f6feb]' : 'hover:bg-[#21262d]'
          )}
        >
          <span className={clsx(
            'text-xs px-1.5 py-0.5 rounded font-mono',
            item.type === 'keyword' && 'bg-[#8957e5] text-white',
            item.type === 'function' && 'bg-[#1f6feb] text-white',
            item.type === 'variable' && 'bg-[#238636] text-white',
            item.type === 'color' && 'bg-[#d29922] text-black'
          )}>
            {item.type === 'keyword' && 'K'}
            {item.type === 'function' && 'F'}
            {item.type === 'variable' && 'V'}
            {item.type === 'color' && 'C'}
          </span>
          <span className="text-sm text-[#c9d1d9] truncate">{item.label}</span>
        </div>
      ))}
    </div>
  )
}

// Syntax highlighting function
function highlightPineScript(code: string): string {
  let result = code
    // Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  
  // Comments
  result = result.replace(/(\/\/.*$)/gm, '<span class="text-[#8b949e]">$1</span>')
  
  // Strings
  result = result.replace(/(".*?"|'.*?')/g, '<span class="text-[#a5d6ff]">$1</span>')
  
  // Numbers
  result = result.replace(/\b(\d+\.?\d*)\b/g, '<span class="text-[#79c0ff]">$1</span>')
  
  // Functions (ta.*, math.*, etc.)
  const funcPattern = new RegExp(`\\b(${PINE_FUNCTIONS.map(f => f.replace('.', '\\.')).join('|')})\\b`, 'g')
  result = result.replace(funcPattern, '<span class="text-[#d2a8ff]">$1</span>')
  
  // Keywords
  const keywordPattern = new RegExp(`\\b(${PINE_KEYWORDS.join('|')})\\b`, 'g')
  result = result.replace(keywordPattern, '<span class="text-[#ff7b72]">$1</span>')
  
  // Built-in variables
  const varPattern = new RegExp(`\\b(${PINE_VARIABLES.join('|').replace(/\./g, '\\.')})\\b`, 'g')
  result = result.replace(varPattern, '<span class="text-[#ffa657]">$1</span>')
  
  // Colors
  const colorPattern = new RegExp(`\\b(${PINE_COLORS.join('|').replace(/\./g, '\\.')})\\b`, 'g')
  result = result.replace(colorPattern, '<span class="text-[#7ee787]">$1</span>')
  
  // Operators
  result = result.replace(/(\+|-|\*|\/|%|=|==|!=|&lt;|&gt;|&lt;=|&gt;=|&amp;&amp;|\|\||!)/g, '<span class="text-[#79c0ff]">$1</span>')
  
  return result
}

// Function descriptions for autocomplete
function getPineFunctionDescription(func: string): string {
  const descriptions: Record<string, string> = {
    'ta.sma': 'Simple Moving Average',
    'ta.ema': 'Exponential Moving Average',
    'ta.rsi': 'Relative Strength Index',
    'ta.macd': 'MACD (returns tuple)',
    'ta.bb': 'Bollinger Bands (returns tuple)',
    'ta.atr': 'Average True Range',
    'ta.crossover': 'True when series1 crosses over series2',
    'ta.crossunder': 'True when series1 crosses under series2',
    'strategy.entry': 'Enter a position',
    'strategy.exit': 'Exit a position',
    'strategy.close': 'Close position by id',
  }
  return descriptions[func] || ''
}

// Default Pine Script template
const defaultPineScript = `//@version=5
strategy("K.I.T. Momentum Strategy", overlay=true, default_qty_type=strategy.percent_of_equity, default_qty_value=100)

// === INPUTS ===
fast_length = input.int(12, "Fast EMA Length", minval=1)
slow_length = input.int(26, "Slow EMA Length", minval=1)
signal_length = input.int(9, "Signal Length", minval=1)
rsi_length = input.int(14, "RSI Length", minval=1)
rsi_overbought = input.int(70, "RSI Overbought", minval=50, maxval=100)
rsi_oversold = input.int(30, "RSI Oversold", minval=0, maxval=50)

// === INDICATORS ===
[macd_line, signal_line, hist] = ta.macd(close, fast_length, slow_length, signal_length)
rsi = ta.rsi(close, rsi_length)
ema_fast = ta.ema(close, fast_length)
ema_slow = ta.ema(close, slow_length)

// === SIGNALS ===
long_signal = ta.crossover(macd_line, signal_line) and rsi < rsi_overbought
short_signal = ta.crossunder(macd_line, signal_line) and rsi > rsi_oversold

// === STRATEGY ===
if long_signal
    strategy.entry("Long", strategy.long)

if short_signal
    strategy.entry("Short", strategy.short)

// === PLOTS ===
plot(ema_fast, "Fast EMA", color=color.new(color.blue, 0))
plot(ema_slow, "Slow EMA", color=color.new(color.orange, 0))

// Entry markers
plotshape(long_signal, "Long Signal", shape.triangleup, location.belowbar, color.green, size=size.small)
plotshape(short_signal, "Short Signal", shape.triangledown, location.abovebar, color.red, size=size.small)
`

export { defaultPineScript }
